import React, { useCallback, useMemo, useRef, useState } from "react";
import CardForm from "../../components/CardForm";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryParams } from "../../hooks/queryParams";
import queryString from "query-string";
import {
  Box,
  Button,
  Container,
  Flex,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  useDisclosure,
} from "@chakra-ui/react";
import RouterLink from "../../components/RouterLink";
import {
  Invoice,
  InvoiceStatus,
  useCancelInvoiceMutation,
  usePayInvoiceMutation,
} from "../../generated/graphql";
import { useConfirmation } from "../../contexts/ConfirmationContext";
import { AddIcon, ChevronDownIcon } from "@chakra-ui/icons";
import TableListTitle from "../../components/TableList/TableListTitle";
import InvoiceForm from "./InvoiceForm";
import TableInvoice from "../../components/TableInvoice";
import { TableImperativeHandle } from "../../components/DefaultTable";
import { FormMutationType } from "../../components/FullWidthModal/interface";
import ModalFormPay from "../../components/ModalFormPay";
import { IModalFormPay } from "../../components/ModalFormPay/interface";
import moment from "moment";
import { IDateRangeValue } from "../../components/DateRangePicker/interface";
import { getThisYear } from "../../components/DateRangePicker/data";
import { IQueryParams } from "./interface";

const ListInvoice: React.FC<{ status?: InvoiceStatus }> = () => {
  const location = useLocation();

  const { pathname } = location;

  const navigate = useNavigate();

  const [cancelInvoiceMutation] = useCancelInvoiceMutation();

  const {
    isOpen: isOpenForm,
    onOpen: onOpenForm,
    onClose: onCloseForm,
  } = useDisclosure();

  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const { confirmation, setIsLoading, close } = useConfirmation();

  const { queryParams } = useQueryParams<IQueryParams>();

  const onPageChange = useCallback(
    (page: number) => {
      const url = queryString.stringifyUrl({
        url: pathname,
        query: {
          ...queryParams,
          page,
        },
      });

      navigate(url);
    },
    [navigate, pathname, queryParams]
  );

  const onFilterSubmit = useCallback(
    (params: IQueryParams) => {
      const url = queryString.stringifyUrl({
        url: pathname,
        query: {
          ...queryParams,
          ...params,
          page: 1,
        },
      });

      navigate(url);
    },
    [navigate, pathname, queryParams]
  );

  const cancelInvoice = useCallback(
    async (row: Invoice) => {
      confirmation({
        title: "Confirmation",
        description: (
          <Box>
            Invoice with customer{" "}
            <Box as="span" fontWeight="bold">
              {row.customerName}
            </Box>{" "}
            will be canceled. Continue?
          </Box>
        ),
        callback: async () => {
          setIsLoading(true);
          const res = await cancelInvoiceMutation({
            variables: { id: row.id },
          });
          tableRef.current?.updateRow(res.data?.cancelInvoice as Invoice);
          setIsLoading(false);
          close();
        },
      });
    },
    [
      cancelInvoiceMutation,
      close,
      confirmation,
      setIsLoading,
    ]
  );

  const {
    isOpen: isOpenPay,
    onClose: onClosePay,
    onOpen: onOpenPay,
  } = useDisclosure();

  const [payInvoiceMutation] = usePayInvoiceMutation();

  const onPay = async (data: IModalFormPay) => {
    confirmation({
      title: "Confirmation",
      description: (
        <Box>
          Invoice with customer{" "}
          <Box as="span" fontWeight="bold">
            {selectedInvoice?.customerName}
          </Box>{" "}
          will be paid. Continue?
        </Box>
      ),
      callback: async () => {
        setIsLoading(true);
        const res = await payInvoiceMutation({
          variables: { id: selectedInvoice?.id as string, input: data },
        });
        tableRef.current?.updateRow(res.data?.payInvoice as Invoice);
        setIsLoading(false);
        close();
        onClosePay();
        setSelectedInvoice(null);
      },
    });
  };

  const tableRef = useRef<TableImperativeHandle<Invoice>>(null);

  const thisYear = getThisYear();

  const queryDownload = useMemo(() => {
    const params: {
      key?: string;
      status?: string;
      dateRangeFrom?: string;
      dateRangeTo?: string;
    } = {
      key: queryParams?.key || undefined,
      status: queryParams?.status || undefined,
    };

    const dateRanges = queryParams?.dateRange?.split("|");

    if (dateRanges && dateRanges.length === 2) {
      params.dateRangeFrom = moment(dateRanges[0]).format("Y-MM-DD");
      params.dateRangeTo = moment(dateRanges[1]).format("Y-MM-DD");
    } else {
      params.dateRangeFrom = moment(thisYear[0]).format("Y-MM-DD");
      params.dateRangeTo = moment(thisYear[1]).format("Y-MM-DD");
    }

    return queryString.stringify(params);
  }, [queryParams, thisYear]);

  const MemozedTable = useMemo(() => {
    return (
      <TableInvoice
        ref={tableRef}
        page={
          queryParams?.page
            ? parseInt(queryParams.page as unknown as string)
            : 1
        }
        onPageChange={onPageChange}
        filterParams={{
          ...queryParams,
          dateRange: queryParams?.dateRange
            ? (queryParams.dateRange
                .split("|")
                .map((elm) => new Date(elm)) as IDateRangeValue)
            : undefined,
        }}
        onFilterSubmit={(filter) => {
          onFilterSubmit({
            ...filter,
            dateRange: filter.dateRange
              ?.map((elm) => moment(elm).format("YYYY-MM-DD"))
              .join("|"),
          });
        }}
        hasMenu
        menuItems={(row) => {
          const options = [
            <MenuItem as={RouterLink} to={`/invoice/${row.id}`}>
              Detail
            </MenuItem>,
          ];

          if (row.status === InvoiceStatus.Unpaid) {
            options.push(<MenuDivider />);
            options.push(
              <MenuItem
                onClick={() => {
                  setSelectedInvoice(row);
                  onOpenForm();
                }}
              >
                Edit
              </MenuItem>
            );
            options.push(<MenuDivider />);
            options.push(
              <MenuItem
                onClick={() => {
                  setSelectedInvoice(row);
                  onOpenPay();
                }}
              >
                Pay
              </MenuItem>
            );
          }

          if (row.status !== InvoiceStatus.Canceled) {
            options.push(<MenuDivider />);
            options.push(
              <MenuItem
                onClick={() => {
                  cancelInvoice(row);
                }}
              >
                Cancel
              </MenuItem>
            );
          }

          return options;
        }}
      />
    );
  }, [
    queryParams,
    cancelInvoice,
    onFilterSubmit,
    onOpenForm,
    onOpenPay,
    onPageChange,
  ]);

  return (
    <>
      <Container maxW="container.xl">
        <CardForm>
          <TableListTitle
            title="List Invoices"
            actionComponents={
              <Flex gap={3}>
                <Menu>
                  <MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
                    Export
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      as={Link}
                      href={`${process.env.REACT_APP_API_URL}/export-invoices-to-pdf?${queryDownload}`}
                    >
                      PDF
                    </MenuItem>
                    <MenuItem
                      as={Link}
                      href={`${process.env.REACT_APP_API_URL}/export-invoices-to-excel?${queryDownload}`}
                    >
                      Excel
                    </MenuItem>
                  </MenuList>
                </Menu>

                <Button onClick={() => onOpenForm()} leftIcon={<AddIcon />}>
                  Create New Invoice
                </Button>
              </Flex>
            }
          />
          {MemozedTable}
        </CardForm>
      </Container>
      <InvoiceForm
        isOpen={isOpenForm}
        onClose={(res) => {
          if (res?.type === FormMutationType.UPDATE) {
            tableRef.current?.updateRow(res.data as Invoice);
          } else if (res?.type === FormMutationType.CREATE) {
            tableRef.current?.refresh();
            onPageChange(1);
          }

          setSelectedInvoice(null);
          onCloseForm();
        }}
        id={selectedInvoice?.id}
      />
      <ModalFormPay onSubmit={onPay} isOpen={isOpenPay} onClose={onClosePay} />
    </>
  );
};

export default ListInvoice;
